import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { CLIENT_PERMISSIONS, EDIT } from 'shared-permissions';
import * as ROUTES from 'constants/routes.js';
import useClientId from 'hooks/useClientId.js';
import { useFeatureContext } from 'contexts/FeatureContext.jsx';
import useClientPermissions from './useClientPermissions.js';
import { trackingService } from 'services/tracking/TrackingService.js';
import { trackingMetaActions } from 'constants/trackingActions.js';
import useOrgId from './useOrgId.js';
import useQuota from './useQuota.js';

export const useUpsell = ({ featureName, quotaType, usageValue }) => {
  const history = useHistory();
  const clientId = useClientId();
  const orgId = useOrgId();
  const { features, loading: isLoadingFeatures } = useFeatureContext();
  const quotaValue = useQuota({ orgId: quotaType ? orgId : undefined, quotaType }); // Only fetch quota when quotaType is provided by passing orgId or not
  const { hasClientPermission, isLoading: isLoadingPermissions } = useClientPermissions({ clientId });

  const isLoadingQuota = quotaType ? typeof quotaValue === 'undefined' : false;
  const isLoading = isLoadingFeatures || isLoadingQuota || isLoadingPermissions;
  const enabled = (() => {
    if (featureName) {
      return Boolean(!features[featureName]?.enabled && features[featureName]?.canUpsell);
    } else if (quotaType && !isLoadingQuota) {
      return quotaValue !== null && usageValue >= quotaValue;
    }
    return false;
  })();
  const canAccessUpsellPage = hasClientPermission(CLIENT_PERMISSIONS.BILLING_CLIENT, EDIT);

  const buildUpsellRoute = useCallback(() => {
    const queryParams = {
      feature: featureName,
      ...(quotaType && {
        quotaType,
        usageValue,
      }),
    };
    return ROUTES.buildRoute(ROUTES.CLIENT_BILLING_UPGRADE, { clientId }, queryParams);
  }, [clientId, featureName, quotaType, usageValue]);

  const navigateToUpsellPage = useCallback(({ trackingModule, trackingAction = 'click' }) => {
    trackingModule = trackingModule || featureName || quotaType;
    const upsellRoute = buildUpsellRoute();
    trackingService.track({
      eventType: trackingService.buildTrackingEventName(
        trackingModule,
        trackingAction,
        trackingMetaActions.UPSELL,
      ),
    });
    history.push(upsellRoute);
  }, [buildUpsellRoute, history]);

  return {
    enabled,
    isLoading,
    buildUpsellRoute,
    canAccessUpsellPage,
    navigateToUpsellPage,
    quotaValue,
  };
};

